/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IStackProps, PersonaSize } from "@fluentui/react";
import { getStrings } from "@microsoft/shifts-experiences-shared";
import { Moment } from "moment";
import { IMemberEntity, IShiftRequestEntity, ShiftRequestStates } from "sh-models";
import { ECSConfigKey, ECSConfigService } from "sh-services";
import { TeamStore } from "sh-stores";
import { conflictRequestStore } from "sh-stores/sh-requestconflict-store";
import StringsStore from "sh-strings/store";

import ConflictUtils from "../../../../../utility/ConflictUtils";
import DateTimeFormatter, { DateTimeFormatType } from "../../../../../utility/DateTimeFormatter";
import { IUserPersonaProps } from "../../../../common/IUserPersonaProps";
import { RequestConflictProps } from "../../../conflict/RequestConflict";
import { ManagerResponseProps } from "../ManagerResponse/ManagerResponseProps";
import fireAccessibilityAlert from "../../../../accessibilityAlert/lib/actions/fireAccessibilityAlert";

/**
 * Retrieves the conflict props for the request.
 * @param shiftRequest The shift request entity.
 * @returns The conflict props.
 */
export const getConflictProps = (shiftRequest: IShiftRequestEntity): RequestConflictProps | undefined => {
    let props: RequestConflictProps | undefined = undefined;

    if (!shouldShowConflicts(shiftRequest)) {
        return props;
    }

    const { requestShift, requestShiftIdToConflictsMap } = conflictRequestStore();
    const { shiftId } = shiftRequest;
    const shift = requestShift.get(shiftId);
    const entities = ConflictUtils.getRequestConflictEntities(shiftId, requestShiftIdToConflictsMap);

    if (shift && entities?.length > 0) {
        props = {
            conflictEntities: entities,
            shiftRequest: shiftRequest,
            shiftWithConflict: shift,
            memberId: shiftRequest.senderMemberId,
            hasConflicts: entities.length > 0
        };
    }

    return props;
};

const shouldShowConflicts = (shiftRequest: IShiftRequestEntity): boolean => {
    const requestStates = new Set([ShiftRequestStates.WaitingOnManager, ShiftRequestStates.WaitingOnReceiver]);

    return requestStates.has(shiftRequest.state);
};

/**
 * Retrieves the stack props for the container stack.
 * @param onClickRequestCallback The callback for when a request is selected.
 * @param shiftRequestId The shift request ID for a given member.
 * @returns The stack props.
 */
export const getContainerStackProps = (onClickRequestCallback: (memberId: string) => void, checkboxSelected: boolean, displayName: string, shiftRequestId?: string, requestCount?: number): IStackProps | undefined => {
    const isToggleCheckboxVisibilityEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.ToggleCheckboxVisibilityEnabled);

    const onClickRequest = (event: React.MouseEvent<HTMLElement>): void => {
        const _strings = StringsStore().registeredStringModules.get("shiftRequests").strings;
        if (shiftRequestId) {
            onClickRequestCallback(shiftRequestId);
            // Using the `!` beore isSelected because the checkbox state is toggled
            fireAccessibilityAlert(!checkboxSelected ? _strings.get("openShiftCheckboxCheckedAriaLabel").format(displayName) : _strings.get("openShiftCheckboxUncheckedAriaLabel").format(displayName));
            event.preventDefault();
        }
        return;
    };

    return {
        onClick: !isToggleCheckboxVisibilityEnabled || (requestCount !== undefined && requestCount > 1) ? onClickRequest : undefined
    };
};

/**
 * Retrieves the manager response props.
 * @param shiftRequest The shift request entity.
 * @param managerMember The manager member entity.
 * @returns The manager response props.
 */
export const getManagerResponseProps = (shiftRequest: IShiftRequestEntity, managerMember: IMemberEntity): ManagerResponseProps | undefined => {
    const { state } = shiftRequest;

    const managerApproved: boolean = state === ShiftRequestStates.ManagerApproved;
    const managerDeclined: boolean = state === ShiftRequestStates.ManagerDeclined;
    const cancelledOrAutoDeclined: boolean = state === ShiftRequestStates.AutoDeclined || state === ShiftRequestStates.SenderDeclined;

    if (managerApproved || managerDeclined || cancelledOrAutoDeclined) {
        return {
            request: shiftRequest,
            managerMember: managerMember
        };
    }
    return undefined;
};

/**
 * Retrieves the persona props.
 * @param member Member entity.
 * @param creationTime When the request was created.
 * @returns The persona props.
 */
export const getPersonaProps = (member: IMemberEntity, creationTime: Moment): IUserPersonaProps|undefined => {
    if (!member) {
        return undefined;
    }

    const { teamId: currentTeamId } = TeamStore();
    const {userId, displayName, teamId} = member;

    const strings = StringsStore().registeredStringModules.get("shiftRequests").strings;
    const unknownUserString = getStrings("openShiftsExperience", "unavailableUserNameText");

    // TODO (CLM): make 'creationTime' a string instead of passing a Moment object.
    const dayOfRequest = DateTimeFormatter.getDateTimeAsString(creationTime, DateTimeFormatType.Date_DateMonthLong);
    const timeOfRequest = DateTimeFormatter.getDateTimeAsString(creationTime, DateTimeFormatType.Time);
    const requestedText = strings.get("requestedAtLabel").format(dayOfRequest, timeOfRequest);

    return {
        size: PersonaSize.size48,
        userId: userId ? userId : undefined,
        text: displayName ?? unknownUserString,
        secondaryText: requestedText,
        crossLocationTeamId: currentTeamId !== teamId ? teamId : undefined // Show team name only if it is different from the current team.
    };
};